import React, { useState, useEffect, useMemo } from "react";
import useSWR from "swr";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, Empty, StatusBadge } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Plus, X, Search, Zap, RefreshCw, CheckCircle2, AlertTriangle, Wrench, HelpCircle, XCircle, PauseCircle } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

// Kurzlabels für die serverseitigen Such-Treffer (warum taucht die Cloud auf?)
const SEARCH_FIELD_LABEL = {
    cloud_id: "Cloud-ID", name: "Name", primary_domain: "Domain",
    fallback_hostname: "Fallback", domain: "Domain", db_user: "DB-User",
    db_name: "DB-Name", db_host: "DB-Host", s3_bucket: "Bucket",
    s3_access_key: "Access-Key",
};

// Cloud names: lowercase a-z, 0-9, '-' and '_' only (stored lowercased).
const NAME_RE = /^[a-z0-9_-]+$/;
const formatNameError = (raw) => {
    const n = (raw || "").trim().toLowerCase();
    if (!n) return null;
    if (n.length > 64) return "Maximum 64 characters.";
    if (!NAME_RE.test(n))
        return "Lowercase letters (a-z), digits (0-9), hyphen (-) and underscore (_) only.";
    return null;
};

function resolveHealth(health, status) {
    // A suspended cloud is intentionally offline (the LB serves a "gesperrt"
    // splash / non-200), so its poll result would otherwise show red "error".
    // Surface it as a distinct, neutral "suspended" state instead of a fault.
    if (status === "suspended")
        return { state: "suspended", Icon: PauseCircle, color: "#a1a1aa", label: "Suspended — intentionally offline" };
    if (!health)
        return { state: "unknown", Icon: HelpCircle, color: "#71717a", label: "status.php not checked yet" };
    const code = health.http_status;
    const rel = health.last_polled_at ? fmtRel(health.last_polled_at) : "—";
    if (health.maintenance === true || code === 503)
        return { state: "maintenance", Icon: Wrench, color: "#fbbf24", label: `Maintenance mode${code ? ` (HTTP ${code})` : ""} · checked ${rel}` };
    if (health.reachable && code === 200)
        return { state: "ok", Icon: CheckCircle2, color: "#34d399", label: `Online — status.php HTTP 200 · checked ${rel}` };
    if (typeof code === "number" && code >= 500)
        return { state: "error", Icon: AlertTriangle, color: "#f87171", label: `HTTP ${code}! · checked ${rel}` };
    return { state: "unreachable", Icon: XCircle, color: "#f87171", label: `Unreachable${health.error ? ` (${health.error})` : ""} · checked ${rel}` };
}

function HealthDot({ health, status, cloudId, checking, onCheck }) {
    const { state, Icon, color, label } = resolveHealth(health, status);
    return (
        <span className="inline-flex items-center gap-1.5">
            <span title={label} data-testid={`clouds-health-${cloudId}`} data-health={state} className="inline-flex items-center">
                <Icon size={15} style={{ color }} strokeWidth={2.4} />
            </span>
            <button
                type="button"
                data-testid={`clouds-health-check-${cloudId}`}
                className="text-cm-dim hover:text-cm-text transition-colors"
                title="Check now (status.php)"
                onClick={onCheck}
                disabled={checking}
            >
                <RefreshCw size={11} className={checking ? "animate-spin text-cm-cyan" : ""} />
            </button>
        </span>
    );
}

function FleetAmpel({ counts }) {
    const items = [
        { k: "ok", color: "#34d399", label: "Online (HTTP 200)" },
        { k: "problem", color: "#f87171", label: "Error / unreachable (≥500)" },
        { k: "maintenance", color: "#fbbf24", label: "Maintenance / 503" },
        { k: "suspended", color: "#a1a1aa", label: "Suspended" },
        { k: "unknown", color: "#71717a", label: "Not checked yet" },
    ];
    return (
        <div className="flex items-center gap-2.5 px-2.5 py-1 bg-cm-surface2/60 border border-cm-line" data-testid="clouds-fleet-ampel" title="status.php / HTTP-200 fleet status">
            {items.map((it) => (
                <span key={it.k} className="inline-flex items-center gap-1 text-xs text-cm-text" title={it.label} data-testid={`clouds-fleet-${it.k}`}>
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: it.color }} />
                    <span className="font-mono tabular-nums">{counts[it.k] || 0}</span>
                </span>
            ))}
        </div>
    );
}

function NameHint({ raw, status }) {
    const fmtErr = formatNameError(raw);
    return (
        <div className="mt-1 text-[11px] font-mono" data-testid="cloud-name-hint">
            <span className="text-cm-muted">
                Lowercase letters, digits, "-" and "_" only. Stored lowercase &amp;
                must be unique per reseller.
            </span>
            {fmtErr && (
                <div className="text-cm-danger" data-testid="cloud-name-format-error">
                    {fmtErr}
                </div>
            )}
            {!fmtErr && raw && status && status.valid && status.available && (
                <div className="text-cm-success" data-testid="cloud-name-available">
                    ✓ "{status.name}" is available.
                </div>
            )}
            {!fmtErr && status && status.valid && status.available === false && (
                <div className="text-cm-danger" data-testid="cloud-name-taken">
                    ✗ {status.reason}
                </div>
            )}
        </div>
    );
}

export default function CloudsPage() {
    const { data, mutate, isLoading } = useSWR("/api/admin/clouds", fetcher, {
        refreshInterval: 4000,
    });
    const { data: resellers } = useSWR("/api/admin/resellers", fetcher);
    const { data: settings } = useSWR("/api/admin/infra/settings", fetcher);
    // Live NC template coverage — drives the version dropdown below.
    const { data: tmplSummary } = useSWR("/api/admin/infra/templates/summary", fetcher);
    const readyVersions = (tmplSummary?.nc_versions || []).filter((v) => v.ready);
    const latestReady = tmplSummary?.latest_ready;
    const baseImageReady = tmplSummary?.base_image?.ready;
    const nav = useNavigate();
    const [showForm, setShowForm] = useState(false);
    const [showDemoForm, setShowDemoForm] = useState(false);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    // Serverseitige Cross-Collection-Suche (MySQL-User, S3, Domains …) — findet
    // Clouds, die der reine Client-Filter (id/name/domain) nicht abdeckt.
    const [dq, setDq] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDq(q.trim()), 250);
        return () => clearTimeout(t);
    }, [q]);
    const { data: searchData } = useSWR(
        dq.length >= 2 ? `/api/admin/search?q=${encodeURIComponent(dq)}` : null,
        fetcher,
    );
    const [form, setForm] = useState({
        reseller_id: "",
        name: "",
        storage_gb: 50,
        nc_version: "",   // empty = latest ready from /templates
        services: {
            collabora: true,
            notify_push: true,
            talk_hpb: false,
            whiteboard: false,
            recording: false,
        },
        admin_email: "",
        souvera: false,
        souvera_slug: "",
        souvera_mail_domain: "",
        souvera_link: false,
        souvera_desk: false,
        souvera_mail_quota_gb: 50,
        souvera_user: "",
    });
    const [demoForm, setDemoForm] = useState({
        reseller_id: "",
        name: "",
        storage_gb: 50,
        enable_collabora: true,
        nc_version: "",     // empty = let backend auto-resolve to latest
    });
    const [err, setErr] = useState(null);
    const [demoErr, setDemoErr] = useState(null);
    const [demoBusy, setDemoBusy] = useState(false);
    const [demoResult, setDemoResult] = useState(null);
    const [syncBusy, setSyncBusy] = useState(false);
    const [syncMsg, setSyncMsg] = useState(null);
    const [healthAllBusy, setHealthAllBusy] = useState(false);
    const [healthMsg, setHealthMsg] = useState(null);
    const [healthChecking, setHealthChecking] = useState({});
    const [healthOverride, setHealthOverride] = useState({});
    const [nameStatus, setNameStatus] = useState(null);
    const [demoNameStatus, setDemoNameStatus] = useState(null);

    const checkName = async (name, reseller_id, setStatus) => {
        if (!name || !reseller_id || formatNameError(name)) {
            setStatus(null);
            return;
        }
        try {
            const r = await api.get("/api/admin/clouds/validate-name", {
                params: { name, reseller_id },
            });
            setStatus(r.data);
        } catch {
            setStatus(null);
        }
    };

    useEffect(() => {
        if (!showForm) return;
        const t = setTimeout(
            () => checkName(form.name, form.reseller_id, setNameStatus), 400);
        return () => clearTimeout(t);
    }, [form.name, form.reseller_id, showForm]);

    useEffect(() => {
        if (!showDemoForm) return;
        const t = setTimeout(
            () => checkName(demoForm.name, demoForm.reseller_id, setDemoNameStatus), 400);
        return () => clearTimeout(t);
    }, [demoForm.name, demoForm.reseller_id, showDemoForm]);

    const demoNameBad = !!formatNameError(demoForm.name)
        || (demoNameStatus && demoNameStatus.available === false);
    const formNameBad = !!formatNameError(form.name)
        || (nameStatus && nameStatus.available === false);

    const fallbackBase = (settings || []).find(
        (s) => s.key === "fallback_domain_base",
    )?.value;

    const serverMatch = useMemo(() => {
        const m = new Map();
        for (const r of searchData?.results || []) m.set(r.cloud_id, r.matches || []);
        return m;
    }, [searchData]);

    const visible = (data || []).filter((c) => {
        if (statusFilter && c.status !== statusFilter) return false;
        if (q) {
            const local = `${c.id} ${c.name} ${c.primary_domain || ""}`
                .toLowerCase().includes(q.toLowerCase());
            if (!local && !serverMatch.has(c.id)) return false;
        }
        return true;
    });

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            await api.post("/api/admin/clouds/provision-demo", {
                reseller_id: form.reseller_id,
                name: form.name,
                storage_gb: form.storage_gb,
                nc_version: form.nc_version || undefined,
                enable_collabora: form.services.collabora,
                enable_notify_push: form.services.notify_push,
                enable_talk_hpb: form.services.talk_hpb,
                enable_whiteboard: form.services.whiteboard,
                enable_recording: form.services.recording,
                admin_email: form.admin_email || undefined,
                souvera: form.souvera || undefined,
                souvera_slug: form.souvera ? form.souvera_slug : undefined,
                souvera_mail_domain: form.souvera ? form.souvera_mail_domain : undefined,
                souvera_link: form.souvera ? form.souvera_link : undefined,
                souvera_desk: form.souvera ? form.souvera_desk : undefined,
                souvera_mail_quota_gb: form.souvera ? Number(form.souvera_mail_quota_gb || 50) : undefined,
                souvera_user: form.souvera ? Number(form.souvera_user || 0) : undefined,
            });
            setShowForm(false);
            setForm({ ...form, name: "" });
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const submitDemo = async (e) => {
        e.preventDefault();
        setDemoErr(null);
        setDemoResult(null);
        setDemoBusy(true);
        try {
            // Omit nc_version when empty so the backend resolves to "latest_ready"
            const payload = { ...demoForm };
            if (!payload.nc_version) delete payload.nc_version;
            const r = await api.post("/api/admin/clouds/provision-demo", payload);
            setDemoResult(r.data);
            setDemoForm({ ...demoForm, name: "" });
            await mutate();
        } catch (e) {
            setDemoErr(formatApiError(e));
        } finally {
            setDemoBusy(false);
        }
    };

    const syncVmNodes = async () => {
        setSyncBusy(true);
        setSyncMsg(null);
        try {
            const r = await api.post("/api/admin/infra/sync-vm-nodes");
            const n = r.data?.corrected ?? 0;
            setSyncMsg(n === 0
                ? `All ${r.data?.checked_vms ?? 0} VM nodes were already correct.`
                : `${n} VM node(s) corrected (after HA migration). Operations against the VMs work again.`);
            await mutate();
        } catch (e) {
            setSyncMsg("Error: " + formatApiError(e));
        } finally {
            setSyncBusy(false);
        }
    };

    const checkOne = async (cloudId, e) => {
        e?.stopPropagation?.();
        setHealthChecking((m) => ({ ...m, [cloudId]: true }));
        try {
            const r = await api.post(`/api/admin/clouds/${cloudId}/health-check`);
            setHealthOverride((m) => ({ ...m, [cloudId]: r.data }));
        } catch (_e) {
            /* keep last known state on error */
        } finally {
            setHealthChecking((m) => ({ ...m, [cloudId]: false }));
        }
    };

    const checkAll = async () => {
        setHealthAllBusy(true);
        setHealthMsg(null);
        try {
            const r = await api.post("/api/admin/clouds/health-check-all");
            if (r.data?.started === false) {
                setHealthMsg("Fleet check is already running — results will keep coming in.");
            } else {
                setHealthMsg(`Checking ${r.data?.clouds ?? "all"} clouds in the background — the Health column updates as results land…`);
            }
            // Background fleet poll: refetch a few times as results land.
            setTimeout(() => mutate(), 6000);
            setTimeout(() => mutate(), 18000);
            setTimeout(() => { mutate(); setHealthAllBusy(false); }, 35000);
        } catch (e) {
            setHealthMsg("Error: " + formatApiError(e));
            setHealthAllBusy(false);
        }
    };

    const fleetCounts = useMemo(() => {
        const c = { ok: 0, problem: 0, maintenance: 0, suspended: 0, unknown: 0 };
        for (const cloud of (data || [])) {
            const h = healthOverride[cloud.id] ?? cloud.health;
            const st = resolveHealth(h, cloud.status).state;
            if (st === "ok") c.ok++;
            else if (st === "maintenance") c.maintenance++;
            else if (st === "suspended") c.suspended++;
            else if (st === "unknown") c.unknown++;
            else c.problem++;
        }
        return c;
    }, [data, healthOverride]);


    return (
        <div data-testid="clouds-page">
            <PageHeader
                testid="clouds-header"
                title="Managed Clouds"
                subtitle="Abstracted Nextcloud products. Internal resources (CPU/RAM/Redis/LB) computed automatically."
                action={
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="clouds-new-demo-button"
                            className="cm-btn-primary flex items-center gap-2"
                            onClick={() => { setShowDemoForm((v) => !v); setShowForm(false); }}
                            title="1-click full-stack tenant: DB+S3+Redis+VM+LB route"
                        >
                            {showDemoForm ? <X size={14} /> : <Zap size={14} />}
                            {showDemoForm ? "Cancel" : "Provision Demo (1-Click)"}
                        </button>
                        <button
                            data-testid="clouds-new-button"
                            className="cm-btn-secondary flex items-center gap-2"
                            onClick={() => { setShowForm((v) => !v); setShowDemoForm(false); }}
                        >
                            {showForm ? <X size={14} /> : <Plus size={14} />}
                            {showForm ? "Cancel" : "Manual provision"}
                        </button>
                    </div>
                }
            />

            {/* Fleet toolbar: health roll-up + fleet-wide actions */}
            <div className="px-4 sm:px-6 pt-4 flex flex-wrap items-center gap-2">
                <FleetAmpel counts={fleetCounts} />
                <div className="flex items-center gap-2 ml-auto">
                    <button
                        data-testid="clouds-health-check-all-button"
                        className="cm-btn-secondary cm-btn-sm flex items-center gap-2"
                        onClick={checkAll}
                        disabled={healthAllBusy}
                        title="Check every cloud now via status.php / HTTP 200 (background). The check normally runs automatically about every 10 min."
                    >
                        <RefreshCw size={12} className={healthAllBusy ? "animate-spin" : ""} />
                        {healthAllBusy ? "Checking…" : "Check all"}
                    </button>
                    <button
                        data-testid="clouds-sync-nodes-button"
                        className="cm-btn-secondary cm-btn-sm flex items-center gap-2"
                        onClick={syncVmNodes}
                        disabled={syncBusy}
                        title="Reconcile stored Proxmox nodes with reality (cluster/resources). Required after HA migrations (e.g. VM wk05 → wk06), otherwise OCC/app-install/NAS/backup/whiteboard fail."
                    >
                        <RefreshCw size={12} className={syncBusy ? "animate-spin" : ""} />
                        {syncBusy ? "Syncing…" : "Sync VM nodes"}
                    </button>
                </div>
            </div>

            {syncMsg && (
                <div className="mx-4 sm:mx-6 mt-3 cm-card p-3 text-xs text-cm-cyan bg-cm-cyan/5 border border-cm-cyan/40" data-testid="clouds-sync-nodes-result">
                    {syncMsg}
                </div>
            )}

            {healthMsg && (
                <div className="mx-4 sm:mx-6 mt-3 cm-card p-3 text-xs text-cm-success bg-cm-success/5 border border-cm-success/40" data-testid="clouds-health-check-result">
                    {healthMsg}
                </div>
            )}

            {!fallbackBase && (
                <div className="m-4 sm:m-6 cm-card p-3 text-xs text-cm-warning bg-cm-warning/5 border border-cm-warning/40" data-testid="clouds-fallback-warning">
                    ⚠ <strong>Set up the fallback domain first:</strong> Go to Infrastructure → Settings,
                    set <code>fallback_domain_base</code> (e.g. <code>cm.host-on.network</code>), and point a
                    DNS wildcard <code>*.{`<base>`}</code> at one of the LB public IPs. Without this, demo provisioning fails.
                </div>
            )}

            {showDemoForm && (
                <form
                    onSubmit={submitDemo}
                    className="m-4 sm:m-6 cm-card p-5 space-y-4 max-w-3xl cm-fade-in border border-sky-700"
                    data-testid="clouds-demo-form"
                >
                    <div className="flex items-start gap-2">
                        <Zap size={18} className="text-cm-brand mt-1" />
                        <div className="flex-1 text-xs text-cm-muted bg-cm-bg border border-cm-line p-2">
                            <strong className="text-cm-brand">1-Click Demo Tenant.</strong> CM provisions in parallel:
                            MariaDB database + S3 bucket + Redis prefix + Nextcloud VM (in cmtnt0) + LB route. Returns immediately
                            with the fallback URL — the Nextcloud install runs in the background (~10–15 min). Poll the cloud
                            row for <code>status=active</code>.
                            {fallbackBase && (
                                <div className="mt-1 text-cm-success">
                                    URL will be: <code>demo-XXXX.{fallbackBase}</code>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="cm-label">Reseller</label>
                            <select
                                data-testid="clouds-demo-reseller"
                                className="cm-input mt-1"
                                value={demoForm.reseller_id}
                                onChange={(e) => setDemoForm({ ...demoForm, reseller_id: e.target.value })}
                                required
                            >
                                <option value="">— select reseller —</option>
                                {(resellers || []).map((r) => (
                                    <option key={r.id} value={r.id}>{`${r.name} (${r.slug})`}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="cm-label">Cloud name (internal label)</label>
                            <input
                                data-testid="clouds-demo-name"
                                className="cm-input mt-1"
                                value={demoForm.name}
                                onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                                placeholder="acme-demo"
                                required
                            />
                            <NameHint raw={demoForm.name} status={demoNameStatus} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="cm-label">Storage (GB)</label>
                            <input
                                data-testid="clouds-demo-storage"
                                type="number" min="10"
                                className="cm-input mt-1 font-mono"
                                value={demoForm.storage_gb}
                                onChange={(e) => setDemoForm({ ...demoForm, storage_gb: Number(e.target.value) })}
                                required
                            />
                        </div>
                        <div>
                            <label className="cm-label">
                                Nextcloud version
                                {!demoForm.nc_version && latestReady && (
                                    <span className="ml-1 text-emerald-400 normal-case font-normal">
                                        → auto: nc{latestReady} (latest)
                                    </span>
                                )}
                            </label>
                            <select
                                data-testid="clouds-demo-nc-version"
                                className="cm-input mt-1 font-mono"
                                value={demoForm.nc_version}
                                onChange={(e) => setDemoForm({ ...demoForm, nc_version: e.target.value })}
                                disabled={!tmplSummary}
                            >
                                <option value="">— auto (latest = nc{latestReady || "?"}) —</option>
                                {readyVersions.slice().reverse().map((v) => (
                                    <option key={v.version} value={v.version}>
                                        nc{v.version}{v.version === latestReady ? " · latest" : ""}
                                    </option>
                                ))}
                            </select>
                            {tmplSummary && readyVersions.length === 0 && (
                                <div className="mt-1 text-[11px] text-cm-danger font-mono">
                                    ⚠ No installable NC version — no cluster has BOTH
                                    nc{`{N}`}-web + nc{`{N}`}-worker templates registered.
                                    Visit <Link to="/templates" className="text-cm-cyan underline">Templates</Link> first.
                                </div>
                            )}
                            {tmplSummary && !baseImageReady && (
                                <div className="mt-1 text-[11px] text-cm-warning font-mono">
                                    ⚠ Base image `debian13` is missing — Redis/Collabora/Caddy provisioning will fail.
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="cm-label">Options</label>
                        <label className="flex items-center gap-2 text-xs text-zinc-300 mt-2 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="clouds-demo-collabora"
                                checked={demoForm.enable_collabora}
                                onChange={(e) => setDemoForm({ ...demoForm, enable_collabora: e.target.checked })}
                            />
                            <span>Enable Collabora Online (office editing)</span>
                        </label>
                    </div>
                    {demoErr && <div className="text-xs text-cm-danger" data-testid="clouds-demo-error">{demoErr}</div>}
                    {demoResult && (
                        <div className="cm-card p-3 bg-cm-success/5 border border-cm-success/40 text-xs" data-testid="clouds-demo-result">
                            <div className="text-cm-success font-bold mb-1">✓ Provisioning kicked off</div>
                            <div>Cloud ID: <code>{demoResult.cloud_id}</code></div>
                            <div>Fallback URL: <a href={demoResult.url} target="_blank" rel="noreferrer" className="text-cm-cyan underline">{demoResult.url}</a></div>
                            <div className="text-cm-muted mt-1">{demoResult.message}</div>
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={demoBusy || !fallbackBase || demoNameBad}
                        data-testid="clouds-demo-submit"
                        className="cm-btn-primary disabled:opacity-50"
                    >
                        {demoBusy ? "Provisioning…" : "Provision now"}
                    </button>
                </form>
            )}

            {showForm && (
                <form
                    onSubmit={submit}
                    className="m-4 sm:m-6 cm-card p-5 space-y-4 max-w-3xl cm-fade-in"
                    data-testid="clouds-form"
                >
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="cm-label">Reseller</label>
                            <select
                                data-testid="clouds-form-reseller"
                                className="cm-input mt-1"
                                value={form.reseller_id}
                                onChange={(e) => setForm({ ...form, reseller_id: e.target.value })}
                                required
                            >
                                <option value="">— select reseller —</option>
                                {(resellers || []).map((r) => (
                                    <option key={r.id} value={r.id}>
                                        {`${r.name} (${r.slug})`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="cm-label">Cloud name</label>
                            <input
                                data-testid="clouds-form-name"
                                className="cm-input mt-1"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                            />
                            <NameHint raw={form.name} status={nameStatus} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="cm-label">Nextcloud version</label>
                            <select
                                data-testid="clouds-form-nc-version"
                                className="cm-input mt-1 font-mono"
                                value={form.nc_version}
                                onChange={(e) => setForm({ ...form, nc_version: e.target.value })}
                                disabled={!tmplSummary}
                            >
                                <option value="">{latestReady ? `latest (NC ${latestReady})` : "— latest —"}</option>
                                {readyVersions.map((v) => (
                                    <option key={v.version} value={v.version}>{`NC ${v.version}`}</option>
                                ))}
                            </select>
                            {tmplSummary && readyVersions.length === 0 && (
                                <div className="text-xs text-cm-danger mt-1">
                                    No installable version — manage templates under /templates.
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="cm-label">Storage (GB)</label>
                            <input
                                data-testid="clouds-form-storage"
                                type="number"
                                min="10"
                                className="cm-input mt-1 font-mono"
                                value={form.storage_gb}
                                onChange={(e) =>
                                    setForm({ ...form, storage_gb: Number(e.target.value) })
                                }
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="cm-label">Services</label>
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                            {[
                                ["collabora", "Collabora Online"],
                                ["notify_push", "Notify Push"],
                                ["talk_hpb", "Talk HPB"],
                                ["whiteboard", "Whiteboard"],
                                ["recording", "Recording Backend"],
                            ].map(([k, label]) => (
                                <label
                                    key={k}
                                    className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        data-testid={`clouds-form-service-${k}`}
                                        checked={form.services[k]}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                services: { ...f.services, [k]: e.target.checked },
                                            }))
                                        }
                                    />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                        <div className="text-xs text-cm-muted mt-1">
                            Collabora is bound during provisioning; Notify Push / Talk HPB / Whiteboard
                            are enabled after "active" (sequentially).
                        </div>
                    </div>
                    <div>
                        <label className="cm-label">Admin-E-Mail (optional)</label>
                        <input
                            data-testid="clouds-form-admin-email"
                            type="email"
                            className="cm-input mt-1"
                            value={form.admin_email}
                            onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                            placeholder="admin@customer.de"
                        />
                        <div className="text-xs text-cm-muted mt-1">
                            Set on the ncadmin user → "Forgot password" in Nextcloud becomes usable.
                        </div>
                    </div>
                    <div className="cm-card p-3 border border-cm-line2 bg-cm-surface2/60">
                        <label className="flex items-center gap-2 text-sm text-cm-text cursor-pointer font-medium">
                            <input
                                type="checkbox"
                                data-testid="clouds-form-souvera"
                                checked={form.souvera}
                                onChange={(e) => setForm({ ...form, souvera: e.target.checked })}
                            />
                            <span>Enable Souvera Workspace</span>
                        </label>
                        {form.souvera && (
                            <div className="mt-3 space-y-3" data-testid="clouds-form-souvera-fields">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="cm-label">Souvera Slug</label>
                                        <input
                                            data-testid="clouds-form-souvera-slug"
                                            className="cm-input mt-1 font-mono"
                                            value={form.souvera_slug}
                                            onChange={(e) => setForm({ ...form, souvera_slug: e.target.value })}
                                            placeholder="customer-01"
                                            required
                                        />
                                        <div className="text-[11px] text-cm-muted mt-1">
                                            → <code>{(form.souvera_slug || "slug").toLowerCase()}.souvera.work</code> becomes the primary domain.
                                        </div>
                                    </div>
                                    <div>
                                        <label className="cm-label">Mail-Domain (Kunde)</label>
                                        <input
                                            data-testid="clouds-form-souvera-mail-domain"
                                            className="cm-input mt-1 font-mono"
                                            value={form.souvera_mail_domain}
                                            onChange={(e) => setForm({ ...form, souvera_mail_domain: e.target.value })}
                                            placeholder="customerdomain.de"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="cm-label">Total mail storage (GB)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        data-testid="clouds-form-souvera-mail-quota"
                                        className="cm-input mt-1 font-mono w-32"
                                        value={form.souvera_mail_quota_gb}
                                        onChange={(e) => setForm({ ...form, souvera_mail_quota_gb: e.target.value })}
                                        placeholder="50"
                                        required
                                    />
                                    <div className="text-xs text-cm-muted mt-1">Required — total quota (default 50 GB), distributable across mailboxes in Souvera Central.</div>
                                </div>
                                <div>
                                    <label className="cm-label">Souvera users (sold seats)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        data-testid="clouds-form-souvera-user"
                                        className="cm-input mt-1 font-mono w-32"
                                        value={form.souvera_user}
                                        onChange={(e) => setForm({ ...form, souvera_user: e.target.value })}
                                        placeholder="e.g. 25"
                                        required
                                    />
                                    <div className="text-xs text-cm-muted mt-1">Required — set via occ souvera_central:licenses:set (scadmin/ncadmin do not count).</div>
                                </div>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid="clouds-form-souvera-link"
                                            checked={form.souvera_link}
                                            onChange={(e) => setForm({ ...form, souvera_link: e.target.checked })}
                                        />
                                        <span>Souvera Link (Talk/HPB)</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid="clouds-form-souvera-desk"
                                            checked={form.souvera_desk}
                                            onChange={(e) => setForm({ ...form, souvera_desk: e.target.checked })}
                                        />
                                        <span>Souvera Desk (Collabora)</span>
                                    </label>
                                </div>
                                <div className="text-xs text-cm-muted">
                                    Souvera is queued as its own job after the build — the cloud only becomes
                                    "active" once Souvera is fully provisioned too.
                                </div>
                            </div>
                        )}
                    </div>
                    {err && <div className="text-xs text-cm-danger">{err}</div>}
                    <button
                        type="submit"
                        disabled={formNameBad}
                        className="cm-btn-primary disabled:opacity-50"
                        data-testid="clouds-form-submit"
                    >
                        Queue provisioning job
                    </button>
                </form>
            )}

            <div className="px-4 sm:px-6 pb-3 flex items-center gap-2">
                <div className="relative">
                    <Search
                        size={14}
                        className="absolute left-2.5 top-2.5 text-zinc-500"
                        strokeWidth={1.5}
                    />
                    <input
                        data-testid="clouds-search"
                        className="cm-input pl-8 max-w-xs"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search: name, domain, MySQL user, DB name, S3…"
                    />
                </div>
                <select
                    data-testid="clouds-status-filter"
                    className="cm-input max-w-[200px]"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="">all statuses</option>
                    {["pending", "provisioning", "active", "suspended", "updating", "deleting", "error"].map(
                        (s) => (
                            <option key={s}>{s}</option>
                        ),
                    )}
                </select>
                <div className="ml-auto text-xs font-mono text-zinc-500" data-testid="clouds-count">
                    {visible.length} / {data?.length ?? 0}
                </div>
            </div>

            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                {isLoading ? (
                    <Spinner />
                ) : !visible.length ? (
                    <Empty title="No clouds match" hint="Adjust filters or create one." />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">UUID</th>
                                    <th className="cm-th">Domain</th>
                                    <th className="cm-th" title="status.php / HTTP-200 check (about every 10 min)">Health</th>
                                    <th className="cm-th">Status</th>
                                    <th className="cm-th">NC</th>
                                    <th className="cm-th">Storage</th>
                                    <th className="cm-th">Seats</th>
                                    <th className="cm-th">Options</th>
                                    <th className="cm-th">Region</th>
                                    <th className="cm-th">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((c) => (
                                    <tr
                                        key={c.id}
                                        className="cm-tr"
                                        data-testid={`clouds-row-${c.id}`}
                                        onClick={() => nav(`/clouds/${c.id}`)}
                                    >
                                        <td className="cm-td">
                                            <Link
                                                to={`/clouds/${c.id}`}
                                                className="font-mono text-cm-text hover:text-cm-brand"
                                                title={`${c.id}${c.name ? `  ·  ${c.name}` : ""}`}
                                                data-testid={`clouds-uuid-${c.id}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {(c.id || "").split("-")[0] || "—"}
                                            </Link>
                                        </td>
                                        <td className="cm-td font-mono text-xs text-cm-muted">
                                            {c.primary_domain || "—"}
                                            {(() => {
                                                const ms = (serverMatch.get(c.id) || []).filter(
                                                    (m) => !["name", "primary_domain", "cloud_id"].includes(m.field));
                                                if (!ms.length) return null;
                                                return (
                                                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`clouds-match-${c.id}`}>
                                                        {ms.slice(0, 3).map((m, i) => (
                                                            <span
                                                                key={i}
                                                                className="cm-badge cm-badge-info text-[10px] whitespace-nowrap"
                                                                title={`${SEARCH_FIELD_LABEL[m.field] || m.field}: ${m.value}`}
                                                            >
                                                                {SEARCH_FIELD_LABEL[m.field] || m.field}: {m.value}
                                                            </span>
                                                        ))}
                                                        {ms.length > 3 && (
                                                            <span className="text-[10px] text-cm-dim">+{ms.length - 3}</span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="cm-td" onClick={(e) => e.stopPropagation()}>
                                            <HealthDot
                                                health={healthOverride[c.id] ?? c.health}
                                                status={c.status}
                                                cloudId={c.id}
                                                checking={!!healthChecking[c.id]}
                                                onCheck={(e) => checkOne(c.id, e)}
                                            />
                                        </td>
                                        <td className="cm-td">
                                            <div className="flex items-center gap-1.5">
                                                <StatusBadge status={c.status} />
                                                {c.update_required && (
                                                    <span
                                                        data-testid={`clouds-update-required-${c.id}`}
                                                        title="Last Nextcloud update failed — open the cloud and repair."
                                                        className="cm-badge cm-badge-warning text-[10px] whitespace-nowrap"
                                                    >
                                                        Update required
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="cm-td font-mono text-xs">{c.nc_version || "—"}</td>
                                        <td className="cm-td font-mono text-xs">{c.storage_gb} GB</td>
                                        <td className="cm-td font-mono text-xs" data-testid={`clouds-seats-${c.id}`}>
                                            {c.souvera_workspace_enabled ? (
                                                c.souvera_user != null ? (
                                                    <span
                                                        className="cm-badge text-[10px]"
                                                        style={{ borderColor: "#5a3a7a", color: "#b88aff" }}
                                                        title="Sold Souvera users (Collabora per user included)"
                                                    >
                                                        {c.souvera_user} Souvera
                                                    </span>
                                                ) : (
                                                    <span className="text-cm-warning/80" title="Souvera active, but no user count stored">— Souvera</span>
                                                )
                                            ) : c.collabora_user != null ? (
                                                <span className="cm-badge cm-badge-info text-[10px]" title="Sold Collabora users">
                                                    {c.collabora_user} Collabora
                                                </span>
                                            ) : (
                                                <span className="text-cm-dim">—</span>
                                            )}
                                        </td>
                                        <td className="cm-td">
                                            <div className="flex gap-1 flex-wrap">
                                                {Object.entries(c.options || {})
                                                    .filter(([k, v]) => v && k !== "notify_push")
                                                    .map(([k]) => (
                                                        <span
                                                            key={k}
                                                            className="cm-badge cm-badge-info text-[10px]"
                                                            data-testid={`clouds-option-${c.id}-${k}`}
                                                        >
                                                            {k.replace(/_/g, " ")}
                                                        </span>
                                                    ))}
                                            </div>
                                        </td>
                                        <td className="cm-td font-mono text-xs text-cm-muted">{c.region}</td>
                                        <td className="cm-td text-xs text-cm-muted">{fmtRel(c.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
